import { watch } from '@vue/composition-api'

import { useAuth } from '@/composables/useAuth'
import { useCentrifugo } from '@/composables/useCentrifugo'
import {
    DISPATCHER_LISTS_VIEW_PERMISSION,
    FOREMAN_VIEW_PERMISSION,
} from '@/constants/permissions'
import { PRINT_GROUP_RESYNC_CHECK_MS } from '@/modules/dispatcher/constants/print'
import {
    handleDispatcherPrintEvent,
    handleDispatcherPrintGroupEvent,
    resetDispatcherPrintEvents,
    resyncDispatcherPrintGroups,
    resyncStalledDispatcherPrintGroups,
} from '@/modules/dispatcher/services/printStatusEvents'
import store from '@/store'

const PRINT_CHANNEL_PREFIX = 'eds:print'
let activeChannels = []
let resyncTimer = null
let stopConnectionWatch = null
let wasConnected = false

export function usePrintNotifications() {
    const { user, hasPermission } = useAuth()
    const { connect, subscribe, unsubscribe, isConnected } = useCentrifugo()

    const handlePublication = data => {
        if (data?.event === 'print_status') {
            handleDispatcherPrintEvent(data)
            return
        }

        if (data?.event === 'print_group_status') {
            handleDispatcherPrintGroupEvent(data)
            return
        }

        if (
            data?.version === 1 &&
            ['print.result', 'print.error'].includes(data.type)
        ) {
            store.dispatch('foreman/handleInvoicePrintEvent', data)
        }
    }

    const reset = () => {
        activeChannels.forEach(channel => {
            unsubscribe(channel, handlePublication)
        })
        activeChannels = []
        stopConnectionWatch?.()
        stopConnectionWatch = null
        clearInterval(resyncTimer)
        resyncTimer = null
        wasConnected = false
        resetDispatcherPrintEvents()
        store.dispatch('foreman/resetInvoicePrintEvents')
    }

    const init = () => {
        reset()

        const userDivisionIds = [
            ...new Set(
                (user.value?.divisions || [])
                    .map(division => division.id)
                    .filter(Boolean)
            ),
        ]
        const divisionIds = hasPermission(FOREMAN_VIEW_PERMISSION)
            ? userDivisionIds
            : userDivisionIds.slice(0, 1)

        if (
            !divisionIds.length ||
            (!hasPermission(FOREMAN_VIEW_PERMISSION) &&
                !hasPermission(DISPATCHER_LISTS_VIEW_PERMISSION))
        ) {
            return
        }

        if (hasPermission(DISPATCHER_LISTS_VIEW_PERMISSION)) {
            store.dispatch('dispatcher/initPrintGroups')
            resyncStalledDispatcherPrintGroups()
            resyncTimer = setInterval(
                resyncStalledDispatcherPrintGroups,
                PRINT_GROUP_RESYNC_CHECK_MS
            )
            stopConnectionWatch = watch(isConnected, connected => {
                if (connected && wasConnected) resyncDispatcherPrintGroups()
                if (!connected) wasConnected = true
            })
        }

        connect()
        activeChannels = divisionIds.map(divisionId => {
            const channel = `${PRINT_CHANNEL_PREFIX}:${divisionId}`
            subscribe(channel, handlePublication)
            return channel
        })
    }

    return {
        initPrintNotifications: init,
        resetPrintNotifications: reset,
    }
}
